/**
 * R1 W2 actions that span the agent store (segments, review strip) and the AI
 * edits store (notices, suggestion list). Kept out of both stores so neither
 * imports the other's internals and `agent-store.ts` does not grow.
 */

import { ApiRequestError, undoAiEdit, type DraftSegmentStatus } from '@/lib/api'
import { useAgentStore } from '@/stores/agent-store'
import { useAiEditsStore } from '@/stores/ai-edits-store'

/**
 * Undo an applied AI edit. Saves the editor's pending typing first, so a hand
 * edit the server has not seen yet makes Undo refuse (409) instead of being
 * thrown away. The outcome lands on the edit's status. Never throws.
 */
export async function undoAppliedAiEdit(proposalId: string): Promise<void> {
  const edits = useAiEditsStore.getState()
  const edit = edits.edits.find((candidate) => candidate.proposalId === proposalId)
  if (!edit) return
  if (edit.status !== 'applied' && edit.status !== 'failed') return

  edits.setEditStatus(proposalId, 'undoing')

  const flusher = useAgentStore.getState().flushDraftSavesBeforeThreadChange
  if (flusher) {
    try {
      await flusher()
    } catch {
      // Unsaved typing could not be saved: undoing now would put the old text
      // under it. Leave the edit in place and let the user retry.
      useAiEditsStore.getState().setEditStatus(proposalId, 'failed')
      return
    }
  }

  try {
    const result = await undoAiEdit(edit.sessionId, edit.sectionId, proposalId)
    const segment = result.segment
    if (segment) {
      applySegmentToAgentStore([segment.sectionId, edit.sectionId], segment.version, {
        content: segment.content,
        wordCount: segment.wordCount,
        status: segment.status,
      })
    }
    useAiEditsStore.getState().setEditStatus(proposalId, 'undone')
  } catch (error) {
    const code = error instanceof ApiRequestError ? error.code : undefined
    const status = error instanceof ApiRequestError ? error.statusCode : undefined
    if (code === 'PROPOSAL_SECTION_CHANGED') {
      const details = error instanceof ApiRequestError ? error.details : undefined
      const currentVersion = details?.['currentVersion']
      const currentContent = details?.['currentContent']
      if (typeof currentVersion === 'number' && typeof currentContent === 'string') {
        // Only move forward: the editor may already hold a newer save.
        applySegmentToAgentStore([edit.sectionId], currentVersion, { content: currentContent }, true)
      }
      useAiEditsStore.getState().setEditStatus(proposalId, 'conflict')
      return
    }
    if (status === 404) {
      useAiEditsStore.getState().setEditStatus(proposalId, 'gone')
      return
    }
    useAiEditsStore.getState().setEditStatus(proposalId, 'failed')
  }
}

function applySegmentToAgentStore(
  candidateSectionIds: string[],
  version: number,
  updates: { content: string; wordCount?: number; status?: DraftSegmentStatus },
  onlyIfNewer = false,
): void {
  const agent = useAgentStore.getState()
  const target = candidateSectionIds
    .map((sectionId) => agent.segments.find((segment) => segment.sectionId === sectionId))
    .find((segment) => segment !== undefined)
  if (!target) return
  if (onlyIfNewer && target.version >= version) return
  agent.updateSegmentVersion(target.sectionId, version, {
    content: updates.content,
    ...(updates.wordCount !== undefined ? { wordCount: updates.wordCount } : {}),
    ...(updates.status !== undefined ? { status: updates.status } : {}),
  })
}

/**
 * Open a waiting suggestion from the per-document list. When its section is
 * in the open editor, the strip switches to it in place and the call returns
 * true. Otherwise it returns false and the caller navigates to the section,
 * whose session load brings the strip up. A legacy (applied) change on screen
 * is never swapped out: its text is already in the section and it must be
 * decided first.
 */
export function openPendingSuggestion(proposalId: string): boolean {
  const suggestion = useAiEditsStore.getState().suggestions.find((item) => item.proposalId === proposalId)
  if (!suggestion) return false
  const agent = useAgentStore.getState()
  const preview = agent.draftPatchPreview
  if (preview?.proposalId === proposalId) return true
  if (preview && preview.mode !== 'staged') return false
  if (agent.isResolvingDraftPatchPreview) return false
  if (!agent.segments.some((segment) => segment.sectionId === suggestion.sectionId)) return false

  useAgentStore.setState({
    draftPatchPreview: {
      proposalId: suggestion.proposalId,
      sectionId: suggestion.sectionId,
      before: suggestion.before,
      after: suggestion.after,
      appliedPatch: { affectedSectionIds: [suggestion.sectionId] },
      hunkDecisions: {},
      mode: 'staged',
      baseSegmentVersion: suggestion.baseSegmentVersion,
      afterCitationRefs: suggestion.afterCitationRefs,
      afterLibraryCitationRefs: suggestion.afterLibraryCitationRefs,
    },
    writingWorkflowState: 'awaiting_approval',
    pendingMutationKind: 'revising',
  })
  return true
}
